// JavaScript Document

// Scripts written by Jacob Bearce @ Weblinx, Inc.

import "mdn-polyfills/Array.prototype.includes";
import "mdn-polyfills/Object.assign";
import "promise-polyfill/src/polyfill";
import axios from "axios";
import httpBuildQuery from "http-build-query";

let syncing = false;

const PARAMS = {
    ajax: true,
    disable_lazy_load: true,
};

const FORMS = document.querySelectorAll(".product-filter");

/**
 * Set the state of an input based on `PARAMS`
 *
 * @param {object} input
 */
const SET = (input) => {

    let name = input.name;

    /**
     * Reformat an input name if it accepts multiple values
     */
    if (name.match(/\[\]$/)) {
        name = name.split("[]")[0];
    }

    /**
     * Retrieve the `PARAMS` value, if any exists
     */
    const VALUE = {}.hasOwnProperty.call(PARAMS, name) ? PARAMS[name] : null;

    /**
     * Set up the appropriate value for the input
     */
    if (["checkbox", "radio"].includes(input.type)) {
        if (Array.isArray(VALUE)) {
            const INDEX = VALUE.indexOf(input.value);

            if (INDEX !== -1) {
                input.checked = true;
            } else {
                input.checked = false;
            }
        } else {
            const CONTROLS = input.getAttribute("aria-controls");

            if (input.value === VALUE) {
                input.checked = true;
            } else {
                input.checked = false;
            }

            if (CONTROLS) {
                /**
                 * Trigger "change" event for AJAX search
                 */
                if ("createEvent" in document) {
                    const EVENT = document.createEvent("HTMLEvents");
                    EVENT.initEvent("change", false, true);
                    input.dispatchEvent(EVENT);
                } else {
                    input.fireEvent("onchange");
                }
            }
        }
    } else {
        input.value = VALUE;

        /**
         * Sync noUiSlider states
         */
        if (input.dataset.range) {
            const LINKED_ID = input.dataset.range;

            if (LINKED_ID.match(/^sidebar__/)) {
                LINKED_ID.replace(/^sidebar__/, "post__");
            } else if (LINKED_ID.match(/^post__/)) {
                LINKED_ID.replace(/^post__/, "sidebar__");
            }

            const RANGE_INPUT = document.getElementById(LINKED_ID);

            if (RANGE_INPUT && typeof RANGE_INPUT.noUiSlider !== "undefined") {
                const RANGE_VALUE = RANGE_INPUT.noUiSlider.get();

                if (input.name.match(/\[min\]$/)) {
                    RANGE_INPUT.noUiSlider.set([parseInt(VALUE), RANGE_VALUE[1]]);
                } else if (input.name.match(/\[max\]$/)) {
                    RANGE_INPUT.noUiSlider.set([RANGE_VALUE[0], parseInt(VALUE)]);
                }
            }
        }
    }
};

/**
 * Sync inputs for all forms on the page
 *
 * @param {object} input
 */
const SYNC = (input = null) => {


    return new Promise((resolve) => {
        for (let i = 0; i < FORMS.length; i++) {
            /**
             * Skip over the "clicked" form to prevent weirdness
             */
            if (input !== null && FORMS[i].contains(input)) {
                continue;
            }

            const INPUTS = FORMS[i].querySelectorAll(".input, input[type=hidden]");

            INPUTS.forEach((input) => {
                SET(input);
            });
        }

        resolve();
    });
};

/**
 * Build out the `PARAMS` object based on input names
 *
 * @param {object} input
 */
const PARSE = (input) => {
    let name  = input.name;
    let value = null;

    /**
     * Reformat an input name and value if it accepts multiple values
     */
    if (name.match(/\[\]$/)) {
        name  = name.split("[]")[0];
        value = {}.hasOwnProperty.call(PARAMS, name) ? PARAMS[name] : [];
    }

    /**
     * Delete existing key and fail out if the input is disabled
     */
    if (input.disabled) {
        delete PARAMS[name]; return;
    }

    /**
     * Set up the appropriate value for the input
     *
     * Nested if statements are necessary here, otherwise you end up
     * overwriting the array value when you actually want to append.
     */
    if (["checkbox", "radio"].includes(input.type)) {
        if (Array.isArray(value)) {
            const INDEX = value.indexOf(input.value);

            if (input.checked) {
                if (INDEX === -1) {
                    value.push(input.value);
                }
            } else {
                if (INDEX !== -1) {
                    value.splice(INDEX, 1);
                }
            }
        } else {
            if (input.checked) {
                value = input.value;
            } else {
                delete PARAMS[name];
            }
        }
    } else {
        value = input.value;
    }

    /**
     * Add the parameter, unless it's empty
     */
    if (![null, []].includes(value)) {
        PARAMS[name] = value;
    }

    /**
     * If the input controls another, trigger a change event on the other
     */
    const CONTROLS = input.getAttribute("aria-controls");

    if (CONTROLS) {
        const TARGETS = CONTROLS.split(" ");

        if (TARGETS.length > 0) {
            TARGETS.forEach((target) => {
                const INPUT = target ? document.getElementById(target) : false;

                if (INPUT) {
                    if ("createEvent" in document) {
                        // workaround for IE11...
                        if (INPUT.disabled) {
                            INPUT.removeAttribute("disabled");
                        }

                        const EVENT = document.createEvent("HTMLEvents");
                        EVENT.initEvent("change", false, true);
                        INPUT.dispatchEvent(EVENT);

                        // workaround for IE11...
                        if (INPUT.disabled) {
                            INPUT.setAttribute("disabled", "disabled");
                        }
                    } else {
                        INPUT.fireEvent("onchange");
                    }
                }
            });
        }
    }
};

/**
 * Update the URL with the new query parameters
 */
const PUSH_URL = () => {
    const LOCAL_PARAMS = Object.assign({}, PARAMS);

    delete LOCAL_PARAMS["ajax"];
    delete LOCAL_PARAMS["disable_lazy_load"];

    if (LOCAL_PARAMS["size[all]"] === "true") {
        delete LOCAL_PARAMS["size[min]"];
        delete LOCAL_PARAMS["size[max]"];
    }

    const QUERY_STRING = httpBuildQuery(LOCAL_PARAMS);

    history.pushState(LOCAL_PARAMS, "", `${l10n.products_url}?${QUERY_STRING}`);
};

/**
 * Send the request, output the response
 *
 * @param {object} input
 */
const REQUEST = (input = null) => {
    /**
     * If the element controls another, it's not actually used for filtering
     */
    if (input !== null && input.getAttribute("aria-controls")) {
        return;
    }

    /**
     * Don't process if the changed input is the `min` in a combined range, as
     * the `max` input should be processed immediately afterward
     */
    if (input !== null && input.name.match(/\[min\]$/)) {
        return;
    }

    axios.get(l10n.products_url, {
        params: PARAMS
    }).then((response) => {
        const TARGET = document.getElementById("results");

        if (TARGET) {
            TARGET.innerHTML = response.data;
        }

        PUSH_URL();

        /**
         * Sync the inputs, and track when it's syncing to prevent
         * multiple request firing (which causes an infinite loop)
         */
        syncing = true;

        SYNC(input).then(() => {
            syncing = false;
        });
    });
};

/**
 * Update PARAMS on popstate and trigger request
 */
window.onpopstate = (event) => {
    for (const KEY in event.state) {
        PARAMS[KEY] = event.state[KEY];
    }

    REQUEST(null, false);
};

/**
 * Update `PARAMS` when an `input` changes
 *
 * @param {object} input
 */
const LISTEN = (input) => {
    input.addEventListener("change", () => {
        if (!syncing) {
            PARSE(input);
            REQUEST(input);
        }
    });
};

/**
 * Initialize for each input
 */
FORMS.forEach((form) => {
    const INPUTS = form.querySelectorAll(".input, input[type=hidden]");

    INPUTS.forEach((input) => {
        PARSE(input);
        LISTEN(input);
    });
});
